import React, { useState, useEffect } from 'react';
import { Card, CardHeader, TextField, Box, CardContent, Divider, Grid, Typography, Button, FormControl, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const EditStudyPage = () => {
  const { id } = useParams(); // Get the study ID from the URL
  const [editingStudyData, setEditingStudyData] = useState({ title: '', bu_master: '', description: '', category: '', author: '', image: '' });
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [buMasters, setBuMasters] = useState([]);
  const [loading, setLoading] = useState(false); // Loading state
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStudy = async () => {
      setLoading(true); // Start loading
      try {
        const response = await axios.get(`https://3wzg6m6x-5000.asse.devtunnels.ms/api/study/${id}`);
        console.log('Fetched study data:', response.data); // Log the data to inspect structure

        // Map response data to editingStudyData fields
        setEditingStudyData({
          title: response.data.title || '',
          bu_master: response.data.id_bu_master || '', // Adjust if API uses id_bu_master
          description: response.data.description || '',
          category: response.data.category || '',
          author: response.data.author || '',
          image: response.data.image || ''
        });

        setImagePreview(response.data.image);
      } catch (error) {
        console.error('Error fetching study:', error);
      } finally {
        setLoading(false); // Stop loading
      }
    };

    const fetchBuMasters = async () => {
      setLoading(true); // Start loading
      try {
        const response = await axios.get('https://3wzg6m6x-5000.asse.devtunnels.ms/api/bu_master');
        setBuMasters(response.data);
      } catch (error) {
        console.error('Error fetching BU Masters:', error);
      } finally {
        setLoading(false); // Stop loading
      }
    };

    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      setEditingStudyData((prevData) => ({
        ...prevData,
        author: decodedToken.username || 'Unknown',
      }));
    }

    fetchStudy();
    fetchBuMasters();
  }, [id]);

  const handleEditStudy = async () => {
    setLoading(true); // Start loading

    // Validate required fields
    if (!editingStudyData.title || !editingStudyData.bu_master || !editingStudyData.category || !editingStudyData.description || !imageFile) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Fields',
        text: 'Please fill in all required fields, including uploading an image.',
      });
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('id', id);
    formData.append('author', editingStudyData.author); 
    formData.append('id_bu_master', editingStudyData.bu_master);
    formData.append('description', editingStudyData.description);
    formData.append('category', editingStudyData.category);
    formData.append('image', imageFile); 
    formData.append('title', editingStudyData.title);

    try {
      const token = localStorage.getItem('token');
      await axios.put('https://3wzg6m6x-5000.asse.devtunnels.ms/api/study/edit', formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Study updated successfully!',
      });

      navigate('/compro/study-case'); // Redirect after successful edit
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update study!',
      });
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImageFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Card sx={{ borderRadius: 2, boxShadow: 3, padding: 3 }}>
      <CardHeader
        title={<Typography variant="h5" sx={{ textAlign: 'center', fontWeight: 'bold' }}>Edit Study</Typography>}
        sx={{ paddingBottom: 2 }}
      />
      <Divider />
      <CardContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} >
              <TextField
                fullWidth
                label="Title"
                value={editingStudyData.title}
                onChange={(e) => setEditingStudyData({ ...editingStudyData, title: e.target.value })}
                variant="outlined"
                required
                sx={{ borderRadius: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" sx={{ borderRadius: 2 }} required>
                <InputLabel>BU Master</InputLabel>
                <Select
                  value={editingStudyData.bu_master}
                  onChange={(e) => setEditingStudyData({ ...editingStudyData, bu_master: e.target.value })}
                  label="BU Master"
                >
                  {buMasters.map((bu) => (
                    <MenuItem key={bu.id} value={bu.id}>{bu.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <ReactQuill
                value={editingStudyData.description}
                onChange={(value) => setEditingStudyData({ ...editingStudyData, description: value })}
                modules={{
                  toolbar: [
                    [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    ['bold', 'italic', 'underline'],
                    ['link'],
                    [{ 'align': [] }],
                    ['image'],
                  ],
                }}
                style={{ height: '150px', width: '100%' }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6} style={{ marginTop: '56px' }}>
              <TextField
                fullWidth
                label="Category"
                value={editingStudyData.category}
                onChange={(e) => setEditingStudyData({ ...editingStudyData, category: e.target.value })}
                variant="outlined"
                required
                sx={{ borderRadius: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={6} style={{ marginTop: '40px' }}>
              <TextField
                label="Author"
                variant="outlined"
                fullWidth
                margin="normal"
                value={editingStudyData.author}
                InputProps={{
                  readOnly: true,
                }}
                sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', borderRadius: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <input type="file" onChange={handleImageChange} style={{ display: 'none' }} id="image-upload" required />
              <label htmlFor="image-upload">
                <Button variant="outlined" fullWidth component="span" sx={{ borderRadius: 2, boxShadow: 2 }}>
                  Upload Image
                </Button>
              </label>
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    width: '100%',
                    maxWidth: '200px',
                    height: 'auto',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    marginTop: '16px',
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                  }}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={handleEditStudy} sx={{ borderRadius: 2, boxShadow: 2 }}>Save</Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default EditStudyPage;
